import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import { urlEndPoint } from "helpers/GeneralHelper";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import TableContainer from "components/Common/TableContainer";
import { submitMailTemplateFilterData } from "model/MailTemplateModel";
import { useMailTemplateContext } from "libs/context/MailTemplateContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

const MailTemplate = () => {
    const { permissionData } = usePermissionContext() || {};
    const navigate = useNavigate();
    const { fetchMailTemplateContextData, mailTemplateContextData } = useMailTemplateContext() || {};

    const [searchQuery, setSearchQuery] = useState("");
    const [mailTemplateData, setMailTemplateData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);

    const filteredData = useMemo(() => {
        if (!searchQuery?.trim()) return mailTemplateData;

        return mailTemplateData?.filter((item) =>
            Object.values(item).some((val) =>
                String(val).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [mailTemplateData]);

    const mailTemplateActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.MailTemplateAddAccess),
            canEdit: Boolean(permissionData?.MailTemplateUpdateAccess),
        }),
        [permissionData]
    );

    const hasAnyMailTemplateRowAction = useMemo(
        () => mailTemplateActionPermissions.canEdit,
        [mailTemplateActionPermissions]
    );

    useEffect(() => {
        fetchMailTemplateContextData();
        getMailTemplateList();
    }, []);

    useEffect(() => {
        const valueReModified = mailTemplateContextData?.table?.columns?.map((col) => {
            const field = col?.field;
            const normalizedField = String(field ?? "").trim().toLowerCase();
            const isStatusColumn =
                normalizedField === "status" ||
                normalizedField === "statusid" ||
                String(col?.type ?? "").trim().toLowerCase() === "status";

            return {
                Header: col?.title,
                accessor: isStatusColumn ? "statusId" : field,
                type: col?.type,
            };
        });
        setTableMetadata(valueReModified);
    }, [mailTemplateContextData]);

    const getMailTemplateList = async (payload = {}) => {
        setLoading(true);
        const response = await submitMailTemplateFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];

        if (response) {
            setMailTemplateData(Array.isArray(listData) ? listData : []);
        } else {
            setMailTemplateData([]);
        }
        setLoading(false);
    };

    const handleFilterSubmit = async (data) => {
        await getMailTemplateList(data);
        setCommonFilterModalOpen(false);
    };

    const handleSearchSubmit = async (value) => {
        await getMailTemplateList({ search: value?.trim() || "" });
    };

    const handleEdit = (row) => {
        const templateId = row?.id ?? row?.Id;
        if (!templateId) return;
        const templateName = row?.name ?? row?.Name ?? row?.templateName ?? "";
        navigate(`/${urlEndPoint.addEditMail}/${templateId}`, {
            state: { templateName },
        });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Mail Template</h5>
                                {mailTemplateActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() => navigate(`/${urlEndPoint.addEditMail}`)}
                                        >
                                            + New Template
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e?.target?.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />
                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {!loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyMailTemplateRowAction}
                                    isEditAccess={mailTemplateActionPermissions.canEdit}
                                    onEdit={
                                        mailTemplateActionPermissions.canEdit ? handleEdit : undefined
                                    }
                                    isCheckboxLeftSide={false}
                                    isPagination={false}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName={"MailTemplate"}
                                />
                            ) : (
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <AllCommonFilterModal
                        setCommonFilterModalOpen={setCommonFilterModalOpen}
                        commonFilterModalOpen={commonFilterModalOpen}
                        modalTitle="Mail Template"
                        handleSubmit={handleFilterSubmit}
                        filterData={mailTemplateContextData?.filters}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

MailTemplate.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(MailTemplate);
